import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X, Pencil, Check } from "lucide-react";
import { useMarathiEdit } from "@/context/MarathiEditContext";

/**
 * DocumentPreviewDialog — English + Marathi document previews with:
 *  - Diagonal ADVSTUDIO.IN watermark behind the document.
 *  - Text selection & copy disabled inside the preview body. In the
 *    Marathi preview, editable translation spans (`.editable-mr`) remain
 *    selectable/editable so the inline-edit feature keeps working.
 *  - The preview stays fully visible and readable at all times. NO blur,
 *    black-screen, DevTools/screen-capture detection, or focus blocking.
 *  - The watermark exists only inside this dialog, NOT inside the
 *    off-screen .pdf-render-host used by html2canvas for PDF generation,
 *    so the downloaded PDF is unaffected.
 */
export default function DocumentPreviewDialog({
  open,
  onOpenChange,
  title,
  onDownload,
  children,
  testId,
  isMarathi = false,
}) {
  const marathiCtx = useMarathiEdit();
  const editMode = isMarathi && marathiCtx?.editMode;
  const bodyRef = useRef(null);

  // Block copy/cut when the event target is inside the preview body,
  // EXCEPT when the target is inside a `.editable-mr` span (Marathi
  // inline-edit fields — those must remain fully editable/copyable).
  useEffect(() => {
    if (!open) return undefined;
    const guard = (e) => {
      const t = e.target;
      if (!t || !bodyRef.current || !bodyRef.current.contains(t)) return;
      if (t.closest && t.closest(".editable-mr")) return;
      e.preventDefault();
    };
    document.addEventListener("copy", guard, true);
    document.addEventListener("cut", guard, true);
    return () => {
      document.removeEventListener("copy", guard, true);
      document.removeEventListener("cut", guard, true);
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[860px] max-h-[90vh] overflow-hidden p-0"
        data-testid={testId}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="text-lg" data-testid={`${testId}-title`}>
                {title}
              </DialogTitle>
              <DialogDescription className="text-xs">
                A4 print preview · 210mm × 297mm
                {editMode ? " · Edit mode ON — click any Marathi text to edit" : ""}
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              {isMarathi && (
                <Button
                  size="sm"
                  variant={editMode ? "default" : "outline"}
                  onClick={marathiCtx.toggleEdit}
                  className="gap-2"
                  data-testid={`${testId}-edit-btn`}
                >
                  {editMode ? (
                    <>
                      <Check className="h-4 w-4" /> Done
                    </>
                  ) : (
                    <>
                      <Pencil className="h-4 w-4" /> Edit
                    </>
                  )}
                </Button>
              )}
              {/* Download PDF button intentionally hidden from the
                  Preview UI for the four civil documents (Vakalatnama
                  English + Marathi, Adjournment Application, Address
                  Memo). The `onDownload` prop and the underlying
                  download API are still wired so payment-gated
                  downloads triggered from outside the preview keep
                  working exactly as before. */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid={`${testId}-close-btn`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div
          ref={bodyRef}
          className="preview-noselect relative overflow-auto bg-[#ecebe6] p-6 max-h-[calc(90vh-80px)]"
          data-testid={`${testId}-body`}
        >
          {/* Diagonal watermark — behind document, ignored by PDF pipeline. */}
          <div className="preview-watermark" aria-hidden="true">
            <span className="preview-watermark-text">ADVSTUDIO.IN</span>
          </div>

          <div style={{ transform: "scale(0.92)", transformOrigin: "top center" }}>
            {children}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
